import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { factorService, strategyMappingService } from '../../services/api';
import CampText from '../CampText';

const CausalFactorsStep = ({ project, factors, onDataChange }) => {
  const { t } = useTranslation();
  const [newFactor, setNewFactor] = useState({
    name: '',
    description: '',
    category: 'political'
  });
  const [loading, setLoading] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  // Les categories comencen **obertes**. Abans començaven totes tancades i el
  // primer que veies en entrar a la passa 1 eren sis acordions buits: la feina
  // que havies fet quedava amagada darrere de sis clics, i la pantalla no
  // servia per al que serveix, que és veure la llista i afegir-hi.
  const [expandedCategories, setExpandedCategories] = useState(null);
  const [strategyElements, setStrategyElements] = useState([]);
  const [loadingStrategy, setLoadingStrategy] = useState(false);
  const [selectedElements, setSelectedElements] = useState(new Set());
  const [showImportSection, setShowImportSection] = useState(false);
  const [showClassificationModal, setShowClassificationModal] = useState(false);
  const [elementsToClassify, setElementsToClassify] = useState([]);
  const [showHelpModal, setShowHelpModal] = useState(false);

  const getCategories = () => {
    const framework = project.category_system || 'PESTEL';
    
    switch (framework) {
      case 'PESTEL':
        return [
          { value: 'political', label: t('factors.pestel.political'), abbr: 'P' },
          { value: 'economic', label: t('factors.pestel.economic'), abbr: 'E' },
          { value: 'social', label: t('factors.pestel.social'), abbr: 'S' },
          { value: 'technological', label: t('factors.pestel.technological'), abbr: 'T' },
          { value: 'environmental', label: t('factors.pestel.environmental'), abbr: 'En' },
          { value: 'legal', label: t('factors.pestel.legal'), abbr: 'L' }
        ];
      case 'PESTE':
        return [
          { value: 'political', label: t('factors.pestel.political'), abbr: 'P' },
          { value: 'economic', label: t('factors.pestel.economic'), abbr: 'E' },
          { value: 'social', label: t('factors.pestel.social'), abbr: 'S' },
          { value: 'technological', label: t('factors.pestel.technological'), abbr: 'T' },
          { value: 'environmental', label: t('factors.pestel.environmental'), abbr: 'En' }
        ];
      case 'PEST':
        return [
          { value: 'political', label: t('factors.pestel.political'), abbr: 'P' },
          { value: 'economic', label: t('factors.pestel.economic'), abbr: 'E' },
          { value: 'social', label: t('factors.pestel.social'), abbr: 'S' },
          { value: 'technological', label: t('factors.pestel.technological'), abbr: 'T' }
        ];
      case 'SWOT':
        return [
          { value: 'strengths', label: t('factors.swot.strengths'), abbr: 'S' },
          { value: 'weaknesses', label: t('factors.swot.weaknesses'), abbr: 'W' },
          { value: 'opportunities', label: t('factors.swot.opportunities'), abbr: 'O' },
          { value: 'threats', label: t('factors.swot.threats'), abbr: 'T' }
        ];
      case 'Cybersecurity Framework':
        return [
          { value: 'identify', label: t('factors.cyber.identify'), abbr: 'ID' },
          { value: 'protect', label: t('factors.cyber.protect'), abbr: 'PR' },
          { value: 'detect', label: t('factors.cyber.detect'), abbr: 'DE' },
          { value: 'respond', label: t('factors.cyber.respond'), abbr: 'RS' },
          { value: 'recover', label: t('factors.cyber.recover'), abbr: 'RC' }
        ];
      case 'Custom':
        // For custom frameworks, use the custom_categories or fall back to PESTEL
        if (project.custom_categories && project.custom_categories.length > 0) {
          return project.custom_categories.map((cat, index) => ({
            value: cat.toLowerCase().replace(/\s+/g, '_'),
            label: cat,
            abbr: cat.charAt(0).toUpperCase()
          }));
        }
        // Fallback to PESTEL if no custom categories defined
        return [
          { value: 'political', label: t('factors.pestel.political'), abbr: 'P' },
          { value: 'economic', label: t('factors.pestel.economic'), abbr: 'E' },
          { value: 'social', label: t('factors.pestel.social'), abbr: 'S' },
          { value: 'technological', label: t('factors.pestel.technological'), abbr: 'T' },
          { value: 'environmental', label: t('factors.pestel.environmental'), abbr: 'En' },
          { value: 'legal', label: t('factors.pestel.legal'), abbr: 'L' }
        ];
      default:
        // Default to PESTEL
        return [
          { value: 'political', label: t('factors.pestel.political'), abbr: 'P' },
          { value: 'economic', label: t('factors.pestel.economic'), abbr: 'E' },
          { value: 'social', label: t('factors.pestel.social'), abbr: 'S' },
          { value: 'technological', label: t('factors.pestel.technological'), abbr: 'T' },
          { value: 'environmental', label: t('factors.pestel.environmental'), abbr: 'En' },
          { value: 'legal', label: t('factors.pestel.legal'), abbr: 'L' }
        ];
    }
  };

  const categories = getCategories();

  // Update default category when project framework changes
  useEffect(() => {
    const firstCategory = categories[0]?.value || 'political';
    setNewFactor(prev => ({
      ...prev,
      category: firstCategory
    }));
  }, [project?.category_system]);

  // Fetch strategy mapping data if this is a case
  useEffect(() => {
    if (project?.workspace_id) {
      fetchStrategyData();
    }
  }, [project?.id]);

  const fetchStrategyData = async () => {
    setLoadingStrategy(true);
    try {
      const data = await strategyMappingService.getStrategyMapping(project.id);
      // Filter only intentions, opportunities, and capabilities
      const relevantElements = (data.elements || []).filter(element => 
        ['intention', 'opportunity', 'capability'].includes(element.element_type)
      );
      setStrategyElements(relevantElements);
    } catch (error) {
      console.error('Error fetching strategy data:', error);
    } finally {
      setLoadingStrategy(false);
    }
  };

  const handleStartClassification = () => {
    if (selectedElements.size === 0) {
      toast.error(t('factors.selectElementsToConvert'));
      return;
    }

    const elementsData = Array.from(selectedElements).map(id => {
      const element = strategyElements.find(e => e.id === id);
      if (element) {
        return {
          ...element,
          category: categories[0]?.value || 'political' // Default to first category
        };
      }
    }).filter(Boolean);

    setElementsToClassify(elementsData);
    setShowClassificationModal(true);
  };

  const handleCategoryChange = (elementId, category) => {
    setElementsToClassify(prev => 
      prev.map(el => el.id === elementId ? { ...el, category } : el)
    );
  };

  const handleConfirmImport = async () => {
    setLoading(true);
    try {
      // Create factors directly instead of using convertToFactors
      const factorsToCreate = elementsToClassify.map(element => ({
        name: element.name,
        description: element.description || '',
        category: element.category,
        source_type: 'strategy_element',
        source_id: element.id,
        ...(project.workspace_id ? { case_id: project.id } : { project_id: project.id })
      }));

      // Create factors one by one
      for (const factorData of factorsToCreate) {
        await factorService.createFactor(factorData);
      }

      toast.success(t('factors.importSuccess', { count: factorsToCreate.length }));
      
      // Reset states
      setSelectedElements(new Set());
      setShowImportSection(false);
      setShowClassificationModal(false);
      setElementsToClassify([]);
      onDataChange();
    } catch (error) {
      toast.error(t('factors.importError'));
      console.error('Error importing factors:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleElementSelection = (id) => {
    const newSelection = new Set(selectedElements);
    if (newSelection.has(id)) {
      newSelection.delete(id);
    } else {
      newSelection.add(id);
    }
    setSelectedElements(newSelection);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newFactor.name.trim()) return;

    setLoading(true);
    try {
      // Check if this is a case (has workspace_id) or a project
      const factorData = project.workspace_id 
        ? { ...newFactor, case_id: project.id }
        : { ...newFactor, project_id: project.id };
        
      await factorService.createFactor(factorData);
      
      setNewFactor({
        name: '',
        description: '',
        category: 'political'
      });
      
      setShowCreateForm(false);
      onDataChange();
    } catch (error) {
      toast.error(t('factors.createError'));
      console.error('Error creating factor:', error);
    } finally {
      setLoading(false);
    }
  };

  const actualitzaFactor = async (factor, canvis) => {
    // Res a fer si no ha canviat: sortir d'un camp sense tocar-lo no ha de
    // generar una petició ni marcar el factor com a modificat.
    const igual = Object.entries(canvis).every(([k, v]) => (factor[k] || '') === (v || ''));
    if (igual) return;
    try {
      await factorService.updateFactor(factor.id, canvis);
      onDataChange();
    } catch (error) {
      console.error('Error updating factor:', error);
      toast.error(t('factors.updateError'));
    }
  };

  const handleDelete = async (factorId) => {
    if (!window.confirm(t('factors.deleteConfirm'))) return;

    try {
      await factorService.deleteFactor(factorId);
      onDataChange();
    } catch (error) {
      toast.error(t('factors.deleteError'));
      console.error('Error deleting factor:', error);
    }
  };

  const factorsByCategory = categories.reduce((acc, category) => {
    acc[category.value] = factors.filter(f => f.category === category.value);
    return acc;
  }, {});

  // Aquí hi havia `getCategoryColor`, que repartia tons d'HSL amb un 10 % de
  // saturació. A aquesta saturació els sis colors surten pràcticament iguals:
  // la rodona de la P i la de la L es distingien tant com dos grisos qualsevol,
  // o sigui que el color no deia res i només feia soroll. Ara la sigla va en
  // una pastilla neutra amb la lletra en monoespaiada, que és el que la
  // distingeix de debò.

  // Quan les categories encara no s'han tocat, totes obertes.
  const obertes = expandedCategories ?? new Set(categories.map(c => c.value));

  const hasStrategyElements = project?.workspace_id && strategyElements.length > 0;

  return (
    <div className="space-y-6">
      {/* Compact Header with Actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-medium text-neutral-900">{t('factors.causalFactors')}</h3>
            <button
              onClick={() => setShowHelpModal(true)}
              className="w-5 h-5 rounded-full bg-neutral-200 hover:bg-neutral-300 flex items-center justify-center text-neutral-500 hover:text-neutral-700 transition-colors"
              title={t('factors.showHelp')}
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </button>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-2xl font-semibold text-accent-600">{factors.length}</span>
            <div className="text-xs text-neutral-500 bg-neutral-100 px-2 py-1 rounded">
              {project?.category_system || 'PESTEL'} ({categories.length})
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          {hasStrategyElements && (
            <button
              onClick={() => setShowImportSection(!showImportSection)}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-accent-600 hover:text-accent-700 border border-accent-300 rounded-lg hover:bg-accent-50 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
              </svg>
              {t('factors.importFromStrategy')} ({strategyElements.length})
            </button>
          )}
          
          <button
            onClick={() => setShowCreateForm(!showCreateForm)}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-accent-600 rounded-lg hover:bg-accent-700 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            {t('factors.addFactor')}
          </button>
        </div>
      </div>

      {/* Strategy Import Section */}
      {showImportSection && hasStrategyElements && (
        <div className="bg-accent-50 rounded-lg border border-accent-200 p-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h4 className="text-sm font-medium text-accent-900">{t('factors.availableStrategyElements')}</h4>
              <p className="text-xs text-accent-700">{t('factors.selectElementsToImport')}</p>
            </div>
            {selectedElements.size > 0 && (
              <button
                onClick={handleStartClassification}
                disabled={loading}
                className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-accent-600 rounded-lg hover:bg-accent-700 disabled:opacity-50 transition-colors"
              >
                {t('factors.classifyAndImport')} ({selectedElements.size})
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {strategyElements.map((element) => (
              <label key={element.id} className="flex items-start gap-3 p-3 bg-white border border-accent-200 rounded-lg hover:bg-accent-50 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedElements.has(element.id)}
                  onChange={() => toggleElementSelection(element.id)}
                  className="mt-1 w-4 h-4 text-accent-600 border-accent-300 rounded focus:ring-accent-500"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                      element.element_type === 'intention' ? 'bg-blue-100 text-blue-700' :
                      element.element_type === 'opportunity' ? 'bg-green-100 text-green-700' :
                      'bg-purple-100 text-purple-700'
                    }`}>
                      {t(`factors.strategicElements.${element.element_type}`)}
                    </span>
                  </div>
                  <h5 className="text-sm font-medium text-neutral-900">{element.name}</h5>
                  {element.description && (
                    <p className="text-xs text-neutral-600 mt-1">{element.description}</p>
                  )}
                </div>
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Create Form */}
      {showCreateForm && (
        <div className="bg-neutral-50 rounded-lg border border-neutral-200 p-4">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  {t('common.name')}
                </label>
                <input
                  type="text"
                  value={newFactor.name}
                  onChange={(e) => setNewFactor({ ...newFactor, name: e.target.value })}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-accent-500 focus:border-accent-500"
                  placeholder={t('factors.namePlaceholder')}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  {t('factors.category')}
                </label>
                <select
                  value={newFactor.category}
                  onChange={(e) => setNewFactor({ ...newFactor, category: e.target.value })}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-accent-500 focus:border-accent-500"
                >
                  {categories.map(cat => (
                    <option key={cat.value} value={cat.value}>{cat.label}</option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                {t('common.description')}
              </label>
              <textarea
                value={newFactor.description}
                onChange={(e) => setNewFactor({ ...newFactor, description: e.target.value })}
                rows={2}
                className="w-full px-3 py-2 border border-neutral-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-accent-500 focus:border-accent-500"
                placeholder={t('factors.descriptionPlaceholder')}
              />
            </div>
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setShowCreateForm(false)}
                className="px-4 py-2 text-sm font-medium text-neutral-600 hover:text-neutral-700 transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 text-sm font-medium text-white bg-accent-600 rounded-md hover:bg-accent-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? t('factors.creating') : t('factors.create')}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Sense cap factor, la pantalla eren sis acordions buits i cap indicació
          de per on començar. */}
      {factors.length === 0 && !showCreateForm && (
        <div className="rounded-lg border border-dashed border-neutral-300 px-4 py-10 text-center">
          <p className="text-sm text-neutral-600">{t('factors.emptyLead')}</p>
          <button
            onClick={() => setShowCreateForm(true)}
            className="mt-3 text-sm font-medium text-accent-700 hover:text-accent-800 hover:underline"
          >
            {t('factors.addFirstFactor')}
          </button>
        </div>
      )}

      {/* Les categories */}
      <div className="space-y-3">
        {categories.length > 1 && (
          <div className="flex justify-end">
            <button
              onClick={() => {
                if (obertes.size === categories.length) {
                  setExpandedCategories(new Set());
                } else {
                  setExpandedCategories(new Set(categories.map(c => c.value)));
                }
              }}
              className="text-xs text-neutral-500 hover:text-neutral-700 transition-colors"
            >
              {obertes.size === categories.length
                ? t('factors.collapseAll')
                : t('factors.expandAll')}
            </button>
          </div>
        )}
        {categories.map((category) => {
          const categoryFactors = factorsByCategory[category.value] || [];
          const isExpanded = obertes.has(category.value);
          
          const toggleCategory = () => {
            const newExpanded = new Set(obertes);
            if (newExpanded.has(category.value)) {
              newExpanded.delete(category.value);
            } else {
              newExpanded.add(category.value);
            }
            setExpandedCategories(newExpanded);
          };
          
          return (
            <div key={category.value} className="bg-white rounded-lg border border-neutral-200 overflow-hidden">
              <button
                onClick={toggleCategory}
                className="w-full px-4 py-3 flex items-center justify-between hover:bg-neutral-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <span className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded border font-mono text-[11px] font-bold ${
                    categoryFactors.length > 0
                      ? 'border-neutral-300 bg-neutral-100 text-neutral-700'
                      : 'border-neutral-200 text-neutral-400'
                  }`}>
                    {category.abbr}
                  </span>
                  <div className="text-left">
                    <h3 className={`text-sm font-medium ${categoryFactors.length > 0 ? 'text-neutral-900' : 'text-neutral-500'}`}>
                      {category.label}
                    </h3>
                    <p className="text-xs text-neutral-500">
                      {t('factors.factorCount', { count: categoryFactors.length })}
                    </p>
                  </div>
                </div>
                <svg
                  className={`w-4 h-4 text-neutral-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              {isExpanded && categoryFactors.length > 0 && (
                <div className="border-t border-neutral-200">
                  <div className="divide-y divide-neutral-100">
                    {categoryFactors.map((factor) => (
                      <div key={factor.id} className="flex items-start gap-3 px-4 py-3 hover:bg-neutral-50">
                        <div className="min-w-0 flex-1 space-y-1.5">
                          {/* El nom i la descripció s'editen aquí mateix, com a
                              la línia de temps del backcasting. Abans un factor
                              només es podia crear i esborrar: una errada al nom
                              obligava a esborrar-lo i tornar-lo a fer —i si ja
                              havia passat el test de drivers, es perdia també
                              el test. Es desa en sortir del camp.
                              (Aquí hi havia també un distintiu «importat /
                              manual» que llegia `factor.source_type`, un camp
                              que no existeix enlloc: no s'ha vist mai.) */}
                          <CampText
                            value={factor.name}
                            onSave={(v) => actualitzaFactor(factor, { name: v.trim() || factor.name })}
                            inputClassName="text-sm font-medium"
                          />
                          <CampText
                            value={factor.description}
                            onSave={(v) => actualitzaFactor(factor, { description: v })}
                            placeholder={t('factors.descriptionPlaceholder')}
                            multiline
                            rows={2}
                            inputClassName="text-xs"
                          />
                        </div>

                        <div className="flex flex-shrink-0 items-center gap-1 pt-1">
                          {/* Canviar un factor de categoria era impossible sense
                              esborrar-lo i tornar-lo a crear. */}
                          <select
                            value={factor.category}
                            onChange={(e) => actualitzaFactor(factor, { category: e.target.value })}
                            aria-label={t('factors.category')}
                            className="rounded-md border border-neutral-300 bg-white px-2 py-1.5 text-xs text-neutral-700 focus:border-accent-500 focus:outline-none focus:ring-2 focus:ring-accent-500/20"
                          >
                            {categories.map(cat => (
                              <option key={cat.value} value={cat.value}>{cat.label}</option>
                            ))}
                          </select>
                          {/* El botó d'esborrar apareixia només en passar-hi el
                              ratolí per sobre: en una tauleta no sortia mai. */}
                          <button
                            onClick={() => handleDelete(factor.id)}
                            title={t('common.delete')}
                            aria-label={t('common.delete')}
                            className="rounded p-1.5 text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-error-dark"
                          >
                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {isExpanded && categoryFactors.length === 0 && (
                <div className="px-4 py-3 text-sm text-neutral-500 border-t border-neutral-200">
                  {t('factors.noFactorsInCategory')}
                </div>
              )}
            </div>
          );
        })}
      </div>


      {/* Classification Modal */}
      {showClassificationModal && (
        <div className="fixed inset-0 bg-neutral-900 bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden">
            <div className="p-6 border-b border-neutral-200">
              <h3 className="text-lg font-medium text-neutral-900">
                {t('factors.classifyElements')}
              </h3>
              <p className="text-sm text-neutral-600 mt-1">
                {t('factors.assignCategories', { framework: project?.category_system || 'PESTEL' })}
              </p>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              <div className="space-y-4">
                {elementsToClassify.map((element) => (
                  <div key={element.id} className="flex items-start gap-4 p-4 bg-neutral-50 rounded-lg">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                          element.element_type === 'intention' ? 'bg-blue-100 text-blue-700' :
                          element.element_type === 'opportunity' ? 'bg-green-100 text-green-700' :
                          'bg-purple-100 text-purple-700'
                        }`}>
                          {t(`factors.strategicElements.${element.element_type}`)}
                        </span>
                      </div>
                      <h4 className="text-sm font-medium text-neutral-900 mb-1">{element.name}</h4>
                      {element.description && (
                        <p className="text-xs text-neutral-600">{element.description}</p>
                      )}
                    </div>
                    
                    <div className="flex-shrink-0 w-48">
                      <label className="block text-xs font-medium text-neutral-700 mb-1">
                        {t('factors.assignToCategory')}
                      </label>
                      <select
                        value={element.category}
                        onChange={(e) => handleCategoryChange(element.id, e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-neutral-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-accent-500 focus:border-accent-500"
                      >
                        {categories.map(cat => (
                          <option key={cat.value} value={cat.value}>{cat.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="p-6 border-t border-neutral-200 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowClassificationModal(false);
                  setElementsToClassify([]);
                }}
                className="px-4 py-2 text-sm font-medium text-neutral-600 hover:text-neutral-800 transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleConfirmImport}
                disabled={loading}
                className="px-6 py-2 text-sm font-medium text-white bg-accent-600 rounded-lg hover:bg-accent-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    {t('factors.importing')}
                  </div>
                ) : (
                  t('factors.importFactors', { count: elementsToClassify.length })
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Help Modal */}
      {showHelpModal && (
        <div className="fixed inset-0 bg-neutral-900 bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-2xl">
            <div className="p-6 border-b border-neutral-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-neutral-900">
                  {t('factors.helpTitle')}
                </h3>
                <button
                  onClick={() => setShowHelpModal(false)}
                  className="text-neutral-400 hover:text-neutral-600 transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            
            <div className="p-6">
              <div className="prose prose-sm max-w-none text-neutral-700">
                <p className="mb-4">{t('factors.stepIntroduction')}</p>
                
                <div className="bg-neutral-50 rounded-lg p-4 mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-2 h-2 bg-accent-500 rounded-full"></div>
                    <strong className="text-sm">{t('factors.framework')}: {project?.category_system || 'PESTEL'}</strong>
                  </div>
                  <p className="text-sm text-neutral-600">
                    {t('factors.categoriesAvailable', { count: categories.length })} - {t('factors.frameworkDescription')}
                  </p>
                </div>

                {hasStrategyElements && (
                  <div className="bg-blue-50 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                      <strong className="text-sm">{t('factors.strategyIntegration')}</strong>
                    </div>
                    <p className="text-sm text-neutral-600">
                      {t('factors.strategyIntegrationDescription')}
                    </p>
                  </div>
                )}
              </div>
            </div>
            
            <div className="px-6 py-4 border-t border-neutral-200 flex justify-end">
              <button
                onClick={() => setShowHelpModal(false)}
                className="px-4 py-2 text-sm font-medium text-white bg-accent-600 rounded-lg hover:bg-accent-700 transition-colors"
              >
                {t('common.close')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CausalFactorsStep;